import type { Router } from 'vue-router'

import nProgress from 'nprogress'

import { createConfigGuard } from '/@/router/guard/configGuard'
import { createDrawerGuard } from '/@/router/guard/drawerGuard'
import { createModalGuard } from '/@/router/guard/modalGuard'
import { createVersionGuard } from '/@/router/guard/versionGuard'
import { AxiosCanceler } from '/@/utils/http/axiosCancel'

/**
 * Guard to prevent routing to .well-known directory
 * This allows server to handle .well-known requests directly
 * @param router
 */
function createWellKnownGuard(router: Router) {
  router.beforeEach(async (to) => {
    // Check if the route starts with /.well-known
    // Prevent Vue Router from handling this route - the server should handle .well-known requests directly
    return !to.path.startsWith('/.well-known')
  })
}

/**
 * Hooks for handling page state
 */
function createPageGuard(router: Router) {
  const loadedPageMap = new Map<string, boolean>()

  router.beforeEach(async (to) => {
    // The page has already been loaded,
    // it will be faster to open it again, you don’t need to do loading and other processing
    to.meta.loaded = !!loadedPageMap.get(to.path)

    return true
  })

  router.afterEach((to) => {
    loadedPageMap.set(to.path, true)
    return true
  })
}

/**
 * The interface used to close the current page to complete the request when the route is switched
 * @param router
 */
function createHttpGuard(router: Router) {
  const axiosCanceler: Nullable<AxiosCanceler> = new AxiosCanceler()

  router.beforeEach(async () => {
    // Switching the route will delete the previous request
    axiosCanceler?.removeAllPending()
    return true
  })
}

/**
 * Show Nprogress during routes changes
 * @param router
 */
export function createProgressGuard(router: Router) {
  router.beforeEach(async () => {
    nProgress.start()
    return true
  })

  router.afterEach(async () => {
    nProgress.done()
    return true
  })
}

// Don't change the order of creation
export function setupRouterGuard(router: Router) {
  createWellKnownGuard(router)
  createPageGuard(router)
  createHttpGuard(router)
  createProgressGuard(router)
  // UI Kit: Auth guards disabled
  // createAuthPermissionGuard(router)
  // createPagePermissionGuard(router)
  // createEarlyAccessFeatureGuard(router)
  // createNoAuthenticationGuard(router)
  createModalGuard(router)
  createDrawerGuard(router)
  createConfigGuard(router)
  createVersionGuard(router)
}
